import { useMemo, useState } from "react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { PackageCard } from "@/components/Cards";
import { useContent } from "@/lib/content";
import { IMG } from "@/lib/site";

const HOLIDAY_TYPES = ["International", "India Holiday", "Honeymoon", "Family", "Adventure", "Luxury", "Group Tour"];
const STYLES = ["Beach", "Culture", "Nature", "Wildlife", "City", "Luxury", "Adventure", "Family"];
const DURATIONS = [
  { label: "3–5 Days", test: (p) => p.days <= 5 },
  { label: "6–8 Days", test: (p) => p.days >= 6 && p.days <= 8 },
  { label: "9+ Days", test: (p) => p.days >= 9 },
];

const BUDGETS = [
  { label: "Under ₹50K", test: (p) => (p.price_from || 0) > 0 && p.price_from < 50000 },
  { label: "₹50K – ₹1L", test: (p) => p.price_from >= 50000 && p.price_from <= 100000 },
  { label: "₹1L – ₹2L", test: (p) => p.price_from > 100000 && p.price_from <= 200000 },
  { label: "Above ₹2L", test: (p) => p.price_from > 200000 },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-royal hover:text-royal"}`}>
      {children}
    </button>
  );
}

export default function PackagesBrowse() {
  const { packages, destinations, loading } = useContent();
  const [dest, setDest] = useState("");
  const [durIdx, setDurIdx] = useState(-1);
  const [hType, setHType] = useState("");
  const [style, setStyle] = useState("");
  const [budgetIdx, setBudgetIdx] = useState(-1);

  const list = useMemo(() => {
    let l = packages;
    if (dest) l = l.filter((p) => p.destination_slug === dest);
    if (durIdx >= 0) l = l.filter(DURATIONS[durIdx].test);
    if (hType) l = l.filter((p) => (p.holiday_types || []).includes(hType));
    if (style) l = l.filter((p) => (p.travel_styles || []).includes(style));
    if (budgetIdx >= 0) l = l.filter(BUDGETS[budgetIdx].test);
    return l;
  }, [packages, dest, durIdx, hType, style, budgetIdx]);

  const usedDests = useMemo(() => {
    const slugs = [...new Set(packages.map((p) => p.destination_slug))];
    return destinations.filter((d) => slugs.includes(d.slug));
  }, [packages, destinations]);

  return (
    <div data-testid="packages-browse-page">
      <SEO title="All Holiday Packages" description="Browse all D Travel Link holiday packages — filter by destination, duration, holiday type and travel style. All costing on request." />
      <PageHero image={IMG.lake} overline="Every Trip, Tailor-Made" title="Browse All Holidays"
                description="Filter by destination, duration, holiday type, travel style and budget. Starting prices are tentative — your final quote is customised to you." />
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-white border border-slate-200/80 p-5 shadow-sm space-y-4" data-testid="package-filters">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 w-28">Destination</span>
              <Chip active={!dest} onClick={() => setDest("")} testid="filter-destination-all">All</Chip>
              {usedDests.map((d) => (
                <Chip key={d.slug} active={dest === d.slug} onClick={() => setDest(dest === d.slug ? "" : d.slug)} testid={`filter-destination-${d.slug}`}>{d.name}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 w-28">Duration</span>
              <Chip active={durIdx === -1} onClick={() => setDurIdx(-1)} testid="filter-duration-all">All</Chip>
              {DURATIONS.map((d, i) => (
                <Chip key={d.label} active={durIdx === i} onClick={() => setDurIdx(durIdx === i ? -1 : i)} testid={`filter-duration-${i}`}>{d.label}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 w-28">Holiday Type</span>
              <Chip active={!hType} onClick={() => setHType("")} testid="filter-type-all">All</Chip>
              {HOLIDAY_TYPES.map((t) => (
                <Chip key={t} active={hType === t} onClick={() => setHType(hType === t ? "" : t)} testid={`filter-type-${t.toLowerCase().replace(/\s+/g, "-")}`}>{t}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 w-28">Travel Style</span>
              <Chip active={!style} onClick={() => setStyle("")} testid="filter-style-all">All</Chip>
              {STYLES.map((s) => (
                <Chip key={s} active={style === s} onClick={() => setStyle(style === s ? "" : s)} testid={`filter-style-${s.toLowerCase()}`}>{s}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-400 w-28">Budget</span>
              <Chip active={budgetIdx === -1} onClick={() => setBudgetIdx(-1)} testid="filter-budget-all">All</Chip>
              {BUDGETS.map((b, i) => (
                <Chip key={b.label} active={budgetIdx === i} onClick={() => setBudgetIdx(budgetIdx === i ? -1 : i)} testid={`filter-budget-${i}`}>{b.label}</Chip>
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-slate-500" data-testid="browse-count">{list.length} holiday{list.length !== 1 ? "s" : ""} found — starting prices are tentative, final quotes customised.</p>
          {loading ? (
            <p className="mt-10 text-slate-400">Loading packages...</p>
          ) : list.length === 0 ? (
            <p className="mt-10 text-slate-500" data-testid="browse-empty">No packages match these filters. Try clearing one, or request a fully customised trip.</p>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {list.map((p, i) => <Reveal key={p.slug} delay={(i % 3) * 0.05}><PackageCard pkg={p} /></Reveal>)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
